/**
 * Forms plugin: captures form funnel metadata without field values.
 */
import {
  classifySensitiveFormControl,
  identifyFormControlField,
  inspectIdentityForm,
} from "../lib/identity-fields";
import type { PluginContext } from "../lib/types";
import { now } from "../lib/utils";

import { registerPlugin } from "./register";

export function createFormsPlugin(ctx: PluginContext) {
  const formStarts = new Map<HTMLFormElement, number>();
  const formLastField = new Map<HTMLFormElement, string>();
  const fieldStarts = new WeakMap<HTMLElement, number>();

  const getFieldKind = (field: HTMLElement | null) => {
    if (!field) return undefined;
    const input = field as HTMLInputElement;
    if (classifySensitiveFormControl(input)) return "sensitive";
    return identifyFormControlField(input) ?? "other";
  };

  const getFieldType = (field: HTMLElement | null) => {
    if (!field) return undefined;
    const input = field as HTMLInputElement;
    if (classifySensitiveFormControl(input)) return "sensitive";
    const type = input.type?.toLowerCase();
    return [
      "checkbox",
      "date",
      "email",
      "number",
      "radio",
      "select-one",
      "tel",
      "text",
      "textarea",
    ].includes(type)
      ? type
      : "other";
  };

  const getFormMeta = (form: HTMLFormElement) => {
    const inspection = inspectIdentityForm(form);
    return {
      field_count: inspection.fieldCount,
      identity_fields_present: inspection.identityFields,
      sensitive_field_present: inspection.sensitiveFields.length > 0,
    };
  };

  const onFocus = (event: Event) => {
    if (!ctx.canTrackCategory("form_analytics")) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const form = target.closest("form") as HTMLFormElement | null;
    if (!form) return;

    const startedAt = formStarts.get(form);
    if (!startedAt) {
      formStarts.set(form, now());
      ctx.track("form_start", getFormMeta(form));
    }

    fieldStarts.set(target, now());
    const fieldKind = getFieldKind(target);
    if (fieldKind) formLastField.set(form, fieldKind);

    ctx.track("form_field_focus", {
      field_kind: fieldKind,
      field_type: getFieldType(target),
    });
  };

  const onBlur = (event: Event) => {
    if (!ctx.canTrackCategory("form_analytics")) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const form = target.closest("form") as HTMLFormElement | null;
    if (!form) return;
    const startedAt = fieldStarts.get(target);
    const fieldKind = getFieldKind(target);

    ctx.track("form_field_blur", {
      field_kind: fieldKind,
      field_type: getFieldType(target),
      focus_ms: startedAt ? Math.max(0, now() - startedAt) : undefined,
    });
  };

  const onSubmit = (event: Event) => {
    const form = event.target as HTMLFormElement | null;
    if (!form) return;
    if (ctx.canTrackCategory("form_analytics")) {
      const startedAt = formStarts.get(form) ?? now();
      const meta = getFormMeta(form);
      ctx.track("form_submit", {
        ...meta,
        // A DOM submit event can precede a failed request or an AJAX handler.
        // Only a server/provider receipt can establish successful delivery.
        submission_stage: "attempt",
        submission_source: "dom_submit",
        completion_time_ms: Math.max(0, now() - startedAt),
      });
    }
    formStarts.delete(form);
    formLastField.delete(form);
  };

  const onInvalid = (event: Event) => {
    if (!ctx.canTrackCategory("form_analytics")) return;
    const target = event.target as HTMLInputElement | null;
    const form = target?.closest("form") as HTMLFormElement | null;
    if (!target || !form) return;
    ctx.track("form_error", {
      field_kind: getFieldKind(target),
      field_type: getFieldType(target),
    });
  };

  const flushAbandonments = () => {
    if (!ctx.canTrackCategory("form_analytics")) return;
    const finishedAt = now();
    for (const [form, startedAt] of formStarts.entries()) {
      const meta = getFormMeta(form);
      ctx.track("form_abandon", {
        ...meta,
        abandon_time_ms: Math.max(0, finishedAt - startedAt),
        last_field: formLastField.get(form),
      });
    }
    formStarts.clear();
    formLastField.clear();
  };

  return {
    onInit() {
      document.addEventListener("focusin", onFocus, { capture: true });
      document.addEventListener("focusout", onBlur, { capture: true });
      document.addEventListener("submit", onSubmit, { capture: true });
      document.addEventListener("invalid", onInvalid, { capture: true });
    },
    onUnload() {
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", onBlur, true);
      document.removeEventListener("submit", onSubmit, true);
      document.removeEventListener("invalid", onInvalid, true);
      flushAbandonments();
    },
  };
}

registerPlugin("forms", createFormsPlugin);
